import {
  Directive,
  ElementRef,
  HostListener,
  Input,
  OnChanges,
  SimpleChanges,
  forwardRef
} from '@angular/core';
import {
  AbstractControl,
  NG_VALIDATORS,
  ValidationErrors,
  Validator
} from '@angular/forms';

@Directive({
  selector: '[appPercentageDirective]',
  providers: [
    {
      provide: NG_VALIDATORS,
      useExisting: forwardRef(() => PercentageDirective),
      multi: true
    }
  ]
})
export class PercentageDirective implements Validator, OnChanges {

  @Input() min: number | string | null = null;
  @Input() max: number | string | null = null;

  private validatorChange?: () => void;

  private regex: RegExp = /^\d+$/;

  private specialKeys: string[] = [
    'Backspace',
    'Tab',
    'End',
    'Home',
    'ArrowLeft',
    'ArrowRight',
    'Delete',
    'Del'
  ];

  constructor(private el: ElementRef) { }

  ngOnChanges(changes: SimpleChanges): void {
    if ((changes['min'] || changes['max']) && this.validatorChange) {
      this.validatorChange();
    }
  }

  registerOnValidatorChange(fn: () => void): void {
    this.validatorChange = fn;
  }

  validate(control: AbstractControl): ValidationErrors | null {
    const value = control.value;

    if (value === null || value === undefined || value === '') {
      return null;
    }

    const numericValue = Number(value);

    if (isNaN(numericValue) || !Number.isInteger(numericValue)) {
      return { percentage: true };
    }

    if (
      this.min !== null &&
      this.min !== undefined &&
      numericValue < Number(this.min)
    ) {
      return {
        min: {
          min: Number(this.min),
          actual: numericValue
        }
      };
    }

    if (
      this.max !== null &&
      this.max !== undefined &&
      numericValue > Number(this.max)
    ) {
      return {
        max: {
          max: Number(this.max),
          actual: numericValue
        }
      };
    }

    return null;
  }

  @HostListener('keydown', ['$event'])
  onKeyDown(event: KeyboardEvent): void {
    if (
      this.specialKeys.includes(event.key) ||
      (event.ctrlKey &&
        ['a', 'c', 'v', 'x'].includes(event.key.toLowerCase()))
    ) {
      return;
    }

    if (event.key === '.' || event.key === ',' || event.key === 'Decimal') {
      event.preventDefault();
      return;
    }

    const current: string = this.el.nativeElement.value || '';
    const selectionStart: number = this.el.nativeElement.selectionStart ?? 0;
    const selectionEnd: number = this.el.nativeElement.selectionEnd ?? 0;

    const next: string = [
      current.slice(0, selectionStart),
      event.key,
      current.slice(selectionEnd)
    ].join('');

    if (!this.regex.test(next) || /^0\d+/.test(next)) {
      event.preventDefault();
      return;
    }

    const nextNumber = Number(next);

    const limitMax = this.max !== null && this.max !== undefined ? Number(this.max) : 100;

    if (nextNumber > limitMax) {
      event.preventDefault();
    }
  }
}
